import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, Layout, message, Modal, Row } from 'antd';
import { startCase } from 'lodash';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';

import ProductSectionServices from 'services/product-section';

import { errHandling } from 'utils';
import queryString from 'query-string';

import './style.scss';

const { Content } = Layout;
const { confirm } = Modal;
const MAX_ACTIVE_PROMOS = 3;

export function ProductSection() {
  const location = useLocation();
  const [currentPage] = useState(1);
  const [loadStatus, setLoadStatus] = useState(false);
  const [productSectionList, setProductSectionList] = useState([]);
  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const buttonAction = (row) => {
    const { id, status } = row;
    return status === 'active' ? (
      <Row className="button-table">
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="danger"
            full
            onClick={handleDeactivate.bind(this, id)}
            outline>
            Deactive
          </PdButton>
        </Col>
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="primary"
            full
            onClick={handleEdit.bind(this, id)}>
            Edit
          </PdButton>
        </Col>
      </Row>
    ) : (
      <Row className="button-table">
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="primary"
            full
            onClick={handleReactivate.bind(this, id)}
            outline>
            Reactive
          </PdButton>
        </Col>
        <Col span={12} className="button">
          <PdButton
            className="height-32"
            type="danger"
            full
            onClick={handleDelete.bind(this, id)}>
            Delete
          </PdButton>
        </Col>
      </Row>
    );
  };

  const statusCell = ({ status }) => (
    <div className="status-indicator">
      <span className={status !== 'inactive' ? 'active' : 'hidden'}>
        {startCase(status)}
      </span>
    </div>
  );

  const columns = [
    {
      name: 'Title',
      selector: row => row.title,
    },
    {
      name: 'Status',
      cell: statusCell,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  async function handleReactivate(sectionId) {
    const activeCount = productSectionList.filter(
      ({ status }) => status === 'active'
    ).length;
    if (activeCount >= MAX_ACTIVE_PROMOS) {
      return message.error(
        'Please deactivate an active promo before activating another promo.'
      );
    }

    setLoadStatus(true);
    try {
      await ProductSectionServices.reactivateProductSection(sectionId);
      fetchProductSection();
      message.success('Product section is reactivated');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function handleDeactivate(sectionId) {
    confirm({
      title: 'Are you sure to deactive this section?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await ProductSectionServices.deactivateProductSection(sectionId);
          fetchProductSection();
          message.success('Product section is deactivated');
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  function handleDelete(sectionId) {
    confirm({
      title: 'Are you sure to delete this section?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await ProductSectionServices.deleteProductSection(sectionId);
          fetchProductSection();
          message.success('Product section is deleted');
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  function handleAdd() {
    navigate('/product-section/create');
  }

  function handleEdit(id) {
    navigate({
      pathname: '/product-section/edit',
      search: `?id=${id}`,
    });
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/stocks',
      search: stringified,
    });
  }

  async function fetchProductSection() {
    setLoadStatus(true);
    try {
      const {
        data: { data = [] },
      } = await ProductSectionServices.getProductSectionList();
      setProductSectionList(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  useEffect(() => {
    fetchProductSection();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="main-container pd-cms-product-section">
      <div className="content">
        <div className="pageTitle">Product Section</div>
        <Content className="pageContent">
          <Row gutter={[18, 36]}>
            <Col lg={{ span: 4, offset: 19 }}>
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={handleAdd}>
                Add Product Section
              </PdButton>
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                orderList={productSectionList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { ProductSection };
